import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import axios from "axios";

const showIssueDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const hkt = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  const month = String(hkt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(hkt.getUTCDate()).padStart(2, "0");
  return `${month}-${day}`;
};

export default function IpadScanner() {
  const [scannedId, setScannedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isScannerCollapsed, setIsScannerCollapsed] = useState(false);
  const [records, setRecords] = useState([]);

  const html5QrCodeRef = useRef(null);
  // Use ref to prevent duplicate scans (avoids stale state)
  const scannedCodesRef = useRef(new Set());

  // Start camera
  useEffect(() => {
    const startScanner = async () => {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 280, height: 280 }, // fixed square scan area
            aspectRatio: 1.0,                   // keep square
          },
          onScanSuccess,
          () => {}
        );
      } catch (err) {
        console.error("Camera error:", err);
        setMessage("Cannot access camera. Please allow camera permission.");
      }
    };

    startScanner();

    return () => {
      if (html5QrCodeRef.current?.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const onScanSuccess = async (decodedText) => {
    // 1. Prevent processing the exact same code again
    if (scannedCodesRef.current.has(decodedText)) {
      return;
    }

    // Mark as scanned immediately
    scannedCodesRef.current.add(decodedText);
    setScannedId(decodedText);
    setLoading(true);
    setMessage("");

    console.log("[IpadScanner] scanned:", decodedText);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/getHwTest?qr=${encodeURIComponent(decodedText)}`
      );
      console.log("[IpadScanner] lookup result:", res.data);

      const fetchedItem = res.data;

      if (!fetchedItem) {
        setMessage("No record found for this QR code.");
        return;
      }

      const studentNameId = `${fetchedItem.student_name} (${fetchedItem.student_id})`;

      const newRecordEntry = {
        ...fetchedItem,
        uniqueKey: Math.random().toString(36).substring(2, 9),
        studentDisplay: studentNameId,
        status: fetchedItem.status || "pending",
        mark: fetchedItem.mark || "",
        qrCode: decodedText, // keep original QR for reference
      };

      setRecords((prev) => [newRecordEntry, ...prev]);
      setMessage("Scanned and loaded successfully!");
    } catch (err) {
      scannedCodesRef.current.delete(decodedText);
      console.error("[IpadScanner] lookup failed:", err.response?.status, err.response?.data || err.message);
      const serverError = err.response?.data?.error;
      const known = [
        ...(err.response?.data?.known_homework || []),
        ...(err.response?.data?.known_tests || []),
      ].filter(Boolean);
      setMessage(
        serverError
          ? `${serverError}${known.length ? ` Known: ${known.join(", ")}` : ""}`
          : "Failed to retrieve record for code: " + decodedText
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (uniqueKey, field, value) => {
    setRecords((prev) =>
      prev.map((rec) =>
        rec.uniqueKey === uniqueKey ? { ...rec, [field]: value } : rec
      )
    );
  };

  const updateHwTestRecords = async () => {
    setLoading(true);
    setMessage("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/update`, {
        records,
      });
      setMessage("All records updated successfully!");
    } catch (err) {
      setMessage("Failed to update records.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear all data on this page?")) {
      setRecords([]);
      setScannedId("");
      scannedCodesRef.current.clear(); // important: allow scanning again
      setMessage("Page cleared successfully.");
    }
  };

  const statusOptions = [
    "pending",
    "handed-in",
    "late-sbumission",
    "waived",
    "needs discussion",
  ];

  return (
    <div style={styles.container}>
      {/* LEFT: Camera */}
      <div
        style={{
          ...styles.left,
          width: isScannerCollapsed ? "60px" : "1",
          flex: isScannerCollapsed ? "0 0 60px" : "1",
        }}
      >
        <button
          onClick={() => setIsScannerCollapsed(!isScannerCollapsed)}
          style={styles.collapseBtn}
          title="Toggle Scanner"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isScannerCollapsed ? (
              <polyline points="9 18 15 12 9 6"></polyline>
            ) : (
              <polyline points="15 18 9 12 15 6"></polyline>
            )}
          </svg>
        </button>

        {!isScannerCollapsed && (
          <div style={styles.scannerContainerInner}>
            <h2 style={styles.heading}>Scan QR Code</h2>

            {/* Fixed size container */}
            <div style={styles.scannerWrapper}>
              <div id="qr-reader" style={styles.scanner}></div>
            </div>

            {scannedId && (
              <p style={styles.scannedText}>Last scanned: {scannedId}</p>
            )}
          </div>
        )}
      </div>

      {/* RIGHT: Data entry */}
      <div style={styles.right}>
        <div style={styles.topBar}>
          <h2 style={styles.heading}>RECENT SCANS & DATA ENTRY</h2>
          <div style={styles.actionButtonsContainer}>
            <button
              onClick={updateHwTestRecords}
              disabled={loading || records.length === 0}
              style={styles.submitBtn}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
            <button onClick={handleClearAll} style={styles.clearBtn}>
              Clear Data
            </button>
          </div>
        </div>

        {loading && <p>Loading...</p>}
        {message && <p style={styles.message}>{message}</p>}

        {records.length > 0 ? (
          <div style={styles.listingContainer}>
            {records.map((record) => {
              const isHw = record.type === "hw";
              const themeColor = isHw ? "#16a34a" : "#2563eb";

              return (
                <div
                  key={record.uniqueKey}
                  style={{ ...styles.card, borderColor: themeColor }}
                >
                  {isHw ? (
                    <div style={styles.cardGrid}>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Student</span>
                        <span style={styles.valueText}>{record.studentDisplay}</span>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>HW Name</span>
                        <span style={styles.valueText}>{record.hw_name}</span>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Homework Due Lesson</span>
                        <span style={styles.valueText}>{record.hw_due_lesson ?? "N/A"}</span>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Status</span>
                        <select
                          value={record.status}
                          onChange={(e) =>
                            handleFieldChange(record.uniqueKey, "status", e.target.value)
                          }
                          style={styles.select}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ ...styles.leftBottomIssueDate, color: themeColor }}>
                        Issue Date: {showIssueDate(record.issue_date)}
                      </div>
                      <div style={{ ...styles.rightBottomCourse, color: themeColor }}>
                        Course: {record.related_course || "N/A"}
                      </div>
                    </div>
                  ) : (
                    <div style={styles.cardGrid}>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Student</span>
                        <span style={styles.valueText}>{record.studentDisplay}</span>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Test Name</span>
                        <span style={styles.valueText}>{record.test_name}</span>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Status</span>
                        <select
                          value={record.status}
                          onChange={(e) =>
                            handleFieldChange(record.uniqueKey, "status", e.target.value)
                          }
                          style={styles.select}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ ...styles.gridCell, borderColor: themeColor }}>
                        <span style={styles.label}>Mark</span>
                        <input
                          type="text"
                          placeholder="Mark"
                          value={record.mark}
                          onChange={(e) =>
                            handleFieldChange(record.uniqueKey, "mark", e.target.value)
                          }
                          style={styles.markInput}
                        />
                      </div>
                      <div style={{ ...styles.leftBottomIssueDate, color: themeColor }}>
                        Issue Date: {showIssueDate(record.issue_date)}
                      </div>
                      <div style={{ ...styles.rightBottomCourse, color: themeColor }}>
                        Course: {record.related_course || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: "#666", marginTop: 40 }}>
            Scan a student QR code to begin loading assignments and tests.
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    fontFamily: "system-ui, sans-serif",
    transition: "all 0.3s ease",
  },
  left: {
    background: "#111",
    color: "white",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 10px",
    position: "relative",
    transition: "all 0.3s ease",
  },
  scannerContainerInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginTop: 40,
  },
  collapseBtn: {
    position: "absolute",
    top: 15,
    left: 15,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 4,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  right: {
    flex: 1,
    background: "#f8fafc",
    padding: 32,
    overflowY: "auto",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  actionButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "flex-end",
  },
  heading: {
    margin: "0",
    fontSize: 22,
  },

  // ===== Fixed square scanner =====
  scannerWrapper: {
    width: 300,
    height: 300,
    borderRadius: 12,
    overflow: "hidden",
    background: "#000",
    position: "relative",
  },
  scanner: {
    width: "300px",
    height: "300px",
  },

  scannedText: {
    marginTop: 16,
    fontSize: 14,
    opacity: 0.8,
  },
  listingContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  card: {
    background: "transparent",
    borderWidth: "2px",
    borderStyle: "solid",
    borderRadius: 8,
    position: "relative",
    paddingBottom: 28,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  gridCell: {
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    boxSizing: "border-box",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#475569",
  },
  valueText: {
    fontSize: 14,
    color: "#1e293b",
    paddingTop: 2,
  },
  markInput: {
    padding: "4px 6px",
    fontSize: 13,
    width: "100%",
    borderRadius: 4,
    border: "1px solid #cbd5e1",
    background: "#fff",
  },
  select: {
    padding: "6px 8px",
    fontSize: 14,
    borderRadius: 4,
    border: "1px solid #cbd5e1",
    background: "#fff",
  },
  leftBottomIssueDate: {
    position: "absolute",
    bottom: 6,
    left: 14,
    fontSize: 11,
    fontWeight: 500,
  },
  rightBottomCourse: {
    position: "absolute",
    bottom: 6,
    right: 14,
    fontSize: 11,
    fontWeight: 500,
  },
  submitBtn: {
    padding: "8px 18px",
    fontSize: 15,
    fontWeight: 600,
    background: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  clearBtn: {
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 500,
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  message: {
    padding: "12px 16px",
    background: "#dcfce7",
    borderRadius: 8,
    color: "#166534",
    marginBottom: 16,
  },
};